import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, UserPlus, ArrowRight, CheckCircle, Copy, X } from 'lucide-react';
import api from '../services/api';
import CompanyInfoStep from './CompanyInfoStep';
import QuestionnaireStep from './QuestionnaireStep';
import ReviewStep from './ReviewStep';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Shared wizard for admin/agent-initiated assessments.
 *  Step 1: Pick existing customer OR quick-create one
 *  Step 2: Pick assessment definition (ESG / DORA / ...)
 *  Step 3: Company info (prefilled from selected customer)
 *  Step 4: Questionnaire
 *  Step 5: Review & Submit  → marks execution as approved + paid + creator-stamped
 */
export default function InitiateAssessmentFlow({ role = 'admin' }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetCustomerId = searchParams.get('customer_id');

  const STEPS = ['Customer', 'Assessment', 'Company Info', 'Questionnaire', 'Review'];
  const [step, setStep] = useState(1);

  // Step 1 state
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', full_name: '', company_name: '', contact_phone: '' });
  const [createError, setCreateError] = useState('');
  const [tempPasswordModal, setTempPasswordModal] = useState(null); // { email, password }

  // Step 2 state
  const [assessmentDefs, setAssessmentDefs] = useState([]);
  const [selectedDef, setSelectedDef] = useState(null);

  // Steps 3-5 state
  const [draftId, setDraftId] = useState(null);
  const [assessmentData, setAssessmentData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(null); // { execution_id }

  const customersEndpoint = role === 'admin' ? '/admin/customers' : '/agent/customers-list';
  const quickCreateEndpoint = role === 'admin' ? '/admin/customers/quick-create' : '/agent/customers/quick-create';
  const linkEndpoint = (id) => role === 'admin' ? `/admin/drafts/${id}/link-customer` : `/agent/drafts/${id}/link-customer`;
  const initiateEndpoint = (id) => role === 'admin' ? `/admin/executions/initiate-from-draft/${id}` : `/agent/executions/initiate-from-draft/${id}`;

  // Load customers + assessment definitions
  useEffect(() => {
    api.request(customersEndpoint).then(setCustomers).catch(() => setCustomers([]));
    api.getPublicAssessments().then(setAssessmentDefs).catch(() => setAssessmentDefs([]));
  }, [customersEndpoint]);

  // Auto-select customer if linked from URL
  useEffect(() => {
    if (presetCustomerId && customers.length > 0 && !selectedCustomer) {
      const c = customers.find(u => u.id === presetCustomerId);
      if (c) setSelectedCustomer(c);
    }
  }, [presetCustomerId, customers, selectedCustomer]);

  const filteredCustomers = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return customers.slice(0, 50);
    return customers.filter(c =>
      (c.email || '').toLowerCase().includes(q) ||
      (c.full_name || '').toLowerCase().includes(q) ||
      (c.company_name || '').toLowerCase().includes(q)
    ).slice(0, 50);
  }, [customers, customerSearch]);

  const handleCreateCustomer = async (e) => {
    e?.preventDefault();
    setCreateError('');
    try {
      const res = await api.request(quickCreateEndpoint, {
        method: 'POST',
        body: JSON.stringify(createForm),
      });
      // Show one-time temp password modal
      setTempPasswordModal({ email: res.email, password: res.temp_password });
      // Add to list and select
      const newCustomer = { id: res.id, email: res.email, full_name: res.full_name, company_name: res.company_name, role: res.role, is_active: true };
      setCustomers(prev => [newCustomer, ...prev]);
      setSelectedCustomer(newCustomer);
      setShowCreate(false);
      setCreateForm({ email: '', full_name: '', company_name: '', contact_phone: '' });
    } catch (err) {
      setCreateError(err.message || 'Failed to create customer');
    }
  };

  const proceedFromCustomer = () => {
    if (!selectedCustomer) return;
    setStep(2);
  };

  const proceedFromAssessment = () => {
    if (!selectedDef) return;
    // Fetch questions for this definition (used by ReviewStep)
    fetch(`${API_URL}/api/questions?assessment_id=${selectedDef.id}`)
      .then(r => r.json())
      .then(data => setQuestions(Array.isArray(data) ? data : []))
      .catch(() => setQuestions([]));
    setStep(3);
  };

  const handleCompanyInfoSubmit = async (info) => {
    // Create a public_assessment draft, then link it to the target customer
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${API_URL}/api/assessments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ company_info: info, definition_id: selectedDef.id }),
    });
    const data = await res.json();
    setDraftId(data.id);
    setAssessmentData({ id: data.id, company_info: info });
    // Link to target customer
    await api.request(linkEndpoint(data.id), {
      method: 'POST',
      body: JSON.stringify({ user_id: selectedCustomer.id }),
    }).catch(() => {});
    setStep(4);
  };

  const handleQuestionnaireNext = (data) => {
    setAssessmentData(data);
    setStep(5);
  };

  const handleSubmitInitiation = async () => {
    if (!draftId) return;
    setSubmitting(true);
    try {
      const res = await api.request(initiateEndpoint(draftId), { method: 'POST' });
      setCompleted(res);
    } catch (err) {
      alert(err.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  // Success screen
  if (completed) {
    const dashUrl = role === 'admin' ? '/admin/executions' : '/agent';
    return (
      <div className="max-w-xl mx-auto py-12 px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-9 h-9 text-emerald-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Assessment Submitted</h2>
        <p className="text-gray-500 mb-6">
          The assessment has been created, approved & marked paid on behalf of <b>{selectedCustomer?.full_name || selectedCustomer?.email}</b>.
          The customer can now view the full report from their dashboard.
        </p>
        <div className="flex gap-3 justify-center">
          <button data-testid="initiate-success-back-btn" onClick={() => navigate(dashUrl)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-2.5 rounded-lg">
            Back to Executions
          </button>
          <button onClick={() => { setCompleted(null); setStep(1); setSelectedCustomer(null); setSelectedDef(null); setDraftId(null); setAssessmentData(null); }}
            className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-6 py-2.5 rounded-lg">
            Initiate Another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`initiate-flow-${role}`} className="max-w-5xl mx-auto py-6 px-4">
      {/* Stepper */}
      <div className="mb-8 flex items-center gap-2">
        {STEPS.map((label, i) => (
          <React.Fragment key={label}>
            <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${step >= i + 1 ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                {step > i + 1 ? <CheckCircle className="w-5 h-5" /> : i + 1}
              </div>
              <span className={`text-[11px] font-medium ${step >= i + 1 ? 'text-emerald-700' : 'text-gray-400'}`}>{label}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mb-5 ${step > i + 1 ? 'bg-emerald-600' : 'bg-gray-200'}`} />}
          </React.Fragment>
        ))}
      </div>

      {/* STEP 1: Customer picker */}
      {step === 1 && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Select Customer</h2>
              <p className="text-sm text-gray-500">Pick an existing customer or create a new one for this assessment.</p>
            </div>
            <button data-testid="initiate-new-customer-btn" onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
              <UserPlus className="w-4 h-4" /> New Customer
            </button>
          </div>

          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input data-testid="initiate-customer-search" value={customerSearch} onChange={e => setCustomerSearch(e.target.value)}
              className="w-full bg-white border border-gray-300 rounded-lg pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="Search by name, email or company..." />
          </div>

          <div className="max-h-96 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-100">
            {filteredCustomers.length === 0 && (
              <div className="p-8 text-center text-sm text-gray-400">No customers match.</div>
            )}
            {filteredCustomers.map(c => (
              <button key={c.id} type="button" onClick={() => setSelectedCustomer(c)}
                data-testid={`initiate-pick-customer-${c.id}`}
                className={`w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-gray-50 transition-colors ${selectedCustomer?.id === c.id ? 'bg-emerald-50' : ''}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{c.full_name || '(no name)'}</p>
                  <p className="text-xs text-gray-500 truncate">{c.email}</p>
                </div>
                <div className="text-xs text-gray-400 truncate max-w-[40%] text-right">{c.company_name || '—'}</div>
              </button>
            ))}
          </div>

          <div className="flex justify-end mt-6">
            <button data-testid="initiate-step1-next" disabled={!selectedCustomer} onClick={proceedFromCustomer}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 rounded-lg font-semibold disabled:opacity-50 flex items-center gap-2">
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* New customer form modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Create Customer</h3>
              <button onClick={() => setShowCreate(false)} className="text-gray-400"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-gray-500 mb-4">A temporary password will be auto-generated. You can share it with the customer once.</p>
            <form onSubmit={handleCreateCustomer} className="space-y-3">
              <input data-testid="quick-create-email" required type="email" placeholder="Email *" value={createForm.email} onChange={e => setCreateForm({ ...createForm, email: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              <input data-testid="quick-create-name" required placeholder="Full Name *" value={createForm.full_name} onChange={e => setCreateForm({ ...createForm, full_name: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              <input data-testid="quick-create-company" placeholder="Company" value={createForm.company_name} onChange={e => setCreateForm({ ...createForm, company_name: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              <input data-testid="quick-create-phone" placeholder="Phone (optional)" value={createForm.contact_phone} onChange={e => setCreateForm({ ...createForm, contact_phone: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              {createError && <p className="text-sm text-red-600">{createError}</p>}
              <button data-testid="quick-create-submit" type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 rounded-lg">Create & Select</button>
            </form>
          </div>
        </div>
      )}

      {/* Temp password modal (shown once after creation) */}
      {tempPasswordModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" data-testid="temp-password-modal">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Customer Created</h3>
            <p className="text-sm text-gray-500 mb-4">Share these credentials with the customer. <b>This password is shown only once.</b></p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
              <p className="text-xs text-gray-500 mb-0.5">Email</p>
              <p className="text-sm font-mono text-gray-900 mb-2 break-all">{tempPasswordModal.email}</p>
              <p className="text-xs text-gray-500 mb-0.5">Temporary Password</p>
              <div className="flex items-center gap-2">
                <p className="text-sm font-mono text-gray-900 flex-1 break-all">{tempPasswordModal.password}</p>
                <button onClick={() => navigator.clipboard.writeText(tempPasswordModal.password)}
                  className="text-emerald-600 hover:text-emerald-700 p-1.5 hover:bg-emerald-50 rounded">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
            <button onClick={() => setTempPasswordModal(null)} data-testid="temp-password-confirm"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-lg font-semibold">
              I've copied them — Continue
            </button>
          </div>
        </div>
      )}

      {/* STEP 2: Assessment picker */}
      {step === 2 && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-1">Pick Assessment</h2>
          <p className="text-sm text-gray-500 mb-4">Which assessment will you fill in for <b>{selectedCustomer?.full_name || selectedCustomer?.email}</b>?</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {assessmentDefs.map(a => (
              <button key={a.id} type="button" onClick={() => setSelectedDef(a)}
                data-testid={`initiate-pick-assessment-${a.id}`}
                className={`text-left border rounded-xl p-4 transition-all ${selectedDef?.id === a.id ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200' : 'border-gray-200 hover:border-gray-300'}`}>
                <p className="text-sm font-bold text-gray-900">{a.name}</p>
                <p className="text-xs text-gray-500 mt-1 line-clamp-2">{a.description || ''}</p>
              </button>
            ))}
            {assessmentDefs.length === 0 && <p className="text-sm text-gray-400 col-span-2">No assessments configured.</p>}
          </div>
          <div className="flex items-center justify-between mt-6">
            <button onClick={() => setStep(1)} className="text-sm text-gray-500 hover:text-gray-700">Back</button>
            <button data-testid="initiate-step2-next" disabled={!selectedDef} onClick={proceedFromAssessment}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 rounded-lg font-semibold disabled:opacity-50 flex items-center gap-2">
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: Company Info (prefilled from customer) */}
      {step === 3 && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <CompanyInfoStep
            color={selectedDef?.color || 'emerald'}
            initialData={{
              company_name: selectedCustomer?.company_name || '',
              contact_email: selectedCustomer?.email || '',
              contact_phone: selectedCustomer?.contact_phone || '',
              primary_nace_code: selectedCustomer?.primary_nace_code || '',
              nace_codes: selectedCustomer?.nace_codes || [],
            }}
            onSubmit={handleCompanyInfoSubmit}
          />
        </div>
      )}

      {/* STEP 4: Questionnaire */}
      {step === 4 && draftId && (
        <QuestionnaireStep
          assessmentId={draftId}
          definitionId={selectedDef.id}
          initialAssessment={assessmentData}
          color={selectedDef?.color || 'emerald'}
          onNext={handleQuestionnaireNext}
        />
      )}

      {/* STEP 5: Review & Submit (overrides default register CTA) */}
      {step === 5 && assessmentData && (
        <div>
          <ReviewStep
            assessmentData={assessmentData}
            questions={questions}
            color={selectedDef?.color || 'emerald'}
            submitLabel="Submit & Approve"
            onNext={handleSubmitInitiation}
          />
          {submitting && <p className="text-center text-sm text-gray-500 mt-4">Finalising...</p>}
        </div>
      )}
    </div>
  );
}
